import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RestoreIcon from "@material-ui/icons/Restore";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";

import List from "../../components/List";
import Sidebar from "../../components/SideBar";
import EventApi from "../../services/event";

// -- Formatters ----------------------------------------------------------------

function formatDocument(cpf) {
    if (!cpf) return "-";
    if (cpf.startsWith("PASS-")) {
        return cpf.replace("PASS-", "");
    }
    const digits = cpf.replace(/\D/g, "");
    if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
}

function formatDate(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// -- Column definitions --------------------------------------------------------

function buildColumns(sponsorsMap) {
    return [
        { title: "Nome", field: "name" },
        { title: "E-mail", field: "email" },
        {
            title: "CPF / Passaporte",
            field: "cpf",
            render: (row) => formatDocument(row.cpf),
        },
        { title: "Empresa", field: "company" },
        { title: "Cargo", field: "role" },
        {
            title: "Patrocinador",
            field: "sponsor_id",
            render: (row) => (row.sponsor_id ? (sponsorsMap[row.sponsor_id] || "-") : ""),
        },
        {
            title: "Data de Inscricao",
            field: "created_at",
            render: (row) => formatDate(row.created_at),
        },
    ];
}

// -- PersonDetail --------------------------------------------------------------

function formatPhone(phone) {
    if (!phone) return "-";
    const d = String(phone).replace(/\D/g, "");
    if (d.length === 13) {
        return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5, 9)}-${d.slice(9)}`;
    }
    if (d.length === 12) {
        return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
    }
    return phone;
}

const DetailRow = ({ label, value }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#9ca3af",
            marginBottom: 2,
        }}>
            {label}
        </div>
        <div style={{
            fontSize: 14,
            color: "#111827",
            wordBreak: "break-word",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "7px 10px",
            minHeight: 34,
        }}>
            {value || <span style={{ color: "#d1d5db" }}>&mdash;</span>}
        </div>
    </div>
);

const SectionHeader = ({ children }) => (
    <div style={{
        fontSize: 11,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "#ef4444",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: 6,
        marginTop: 20,
        marginBottom: 14,
    }}>
        {children}
    </div>
);

function PersonDetail({ person, sponsorsMap }) {
    const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" };

    const isPassport = person.cpf && person.cpf.startsWith("PASS-");
    const docLabel = isPassport ? "Passaporte" : "CPF";
    const docValue = formatDocument(person.cpf);
    const sponsorName = person.sponsor_id ? (sponsorsMap[person.sponsor_id] || person.sponsor_id) : null;

    return (
        <div>
            <SectionHeader>Dados Pessoais</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Nome Completo" value={person.name} />
                <DetailRow label="Nome do Cracha" value={person.name_tag} />
            </div>
            <div style={grid2}>
                <DetailRow label={docLabel} value={docValue} />
                <DetailRow label="Genero" value={person.gender} />
            </div>
            <div style={grid2}>
                <DetailRow label="Estado" value={person.address || (isPassport ? "Estrangeiro" : null)} />
                <DetailRow label="Categoria" value={person.event_category} />
            </div>
            <SectionHeader>Contato</SectionHeader>
            <div style={grid2}>
                <DetailRow label="E-mail" value={person.email} />
                <DetailRow label="Telefone" value={formatPhone(person.phone)} />
            </div>
            <SectionHeader>Dados Profissionais</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Empresa / Instituicao" value={person.company} />
                <DetailRow label="Cargo" value={person.role} />
            </div>
            <div style={grid2}>
                <DetailRow label="Setor" value={person.extras} />
                <DetailRow label="Area" value={person.custom_fields} />
            </div>
            {sponsorName && <DetailRow label="Patrocinador" value={sponsorName} />}
            <SectionHeader>Inscricao</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Data de Inscricao" value={formatDate(person.created_at)} />
                <DetailRow label="ID" value={person.id} />
            </div>
            <DetailRow label="Status" value="Recusado" />
        </div>
    );
}

// -- Component -----------------------------------------------------------------

const RejectedSubscriptions = (props) => {
    const eventId =
        props?.computedMatch?.params?.event_id ||
        props?.match?.params?.event_id ||
        1;

    const tableRef = useRef(null);
    const [sponsorsMap, setSponsorsMap] = useState({});

    useEffect(() => {
        EventApi.listSponsors({ event_id: eventId }).then((resp) => {
            if (resp?.data?.data) {
                const map = {};
                resp.data.data.forEach((s) => { map[s.id] = s.name; });
                setSponsorsMap(map);
            }
        }).catch(() => { });
    }, [eventId]);

    const [restoreDialog, setRestoreDialog] = useState({ open: false, person: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, person: null });
    const [viewPerson, setViewPerson] = useState(null);

    // -- Data fetching ---------------------------------------------------------

    const getRows = async (page = 1, perPage = 10, search = "") => {
        try {
            const params = {
                page,
                perPage,
                payment_status: "rejected",
            };
            if (search) {
                const trimmed = search.trim();
                const onlyDigits = trimmed.replace(/\D/g, "");
                const emailRegex = /^[^\s@]+@[^\s@]+/;
                if (emailRegex.test(trimmed)) {
                    params.email = trimmed;
                } else if (onlyDigits.length >= 6 && trimmed === onlyDigits) {
                    params.cpf = trimmed;
                } else {
                    params.name = trimmed;
                }
            }

            const resp = await EventApi.getAllPersonByEvent(eventId, params);
            if (!resp || !resp.data) return { rows: [], total: 0 };

            const data = resp.data.data || (Array.isArray(resp.data) ? resp.data : []);
            const total = resp.data.total || data.length;
            return { rows: data, total };
        } catch (error) {
            console.error("Erro ao carregar inscricoes recusadas:", error);
            toast.error("Erro ao carregar inscricoes recusadas");
            return { rows: [], total: 0 };
        }
    };

    // -- Restore ---------------------------------------------------------------

    function openRestoreDialog(person) {
        setRestoreDialog({ open: true, person });
    }

    function closeRestoreDialog() {
        setRestoreDialog({ open: false, person: null });
    }

    async function handleRestore() {
        const { person } = restoreDialog;
        closeRestoreDialog();
        try {
            const resp = await EventApi.patchPerson(person.id, { payment_status: null });
            if (resp && resp.data) {
                toast.success("Inscricao restaurada para pendentes com sucesso");
                if (tableRef.current) tableRef.current.onQueryChange();
            } else {
                toast.error("Erro ao restaurar inscricao. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao restaurar:", error);
            toast.error("Erro ao restaurar inscricao. Tente novamente");
        }
    }

    // -- Delete permanente -----------------------------------------------------

    function openDeleteDialog(person) {
        setDeleteDialog({ open: true, person });
    }

    function closeDeleteDialog() {
        setDeleteDialog({ open: false, person: null });
    }

    async function handleDelete() {
        const { person } = deleteDialog;
        closeDeleteDialog();
        try {
            const resp = await EventApi.deletePerson(person.id);
            if (resp && (resp.data || resp.status === 200)) {
                toast.success("Inscricao excluida permanentemente");
                if (tableRef.current) tableRef.current.onQueryChange();
            } else {
                toast.error("Erro ao excluir inscricao. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao excluir:", error);
            toast.error("Erro ao excluir inscricao. Tente novamente");
        }
    }

    // -- Actions ---------------------------------------------------------------

    const actions = [
        {
            icon: () => <VisibilityIcon style={{ color: "#6366f1" }} />,
            tooltip: "Ver dados completos",
            onClick: (e, person) => setViewPerson(person),
        },
        {
            icon: () => <RestoreIcon style={{ color: "#f59e0b" }} />,
            tooltip: "Restaurar para pendentes",
            onClick: (e, person) => openRestoreDialog(person),
        },
        {
            icon: () => <DeleteForeverIcon style={{ color: "#ef4444" }} />,
            tooltip: "Excluir permanentemente",
            onClick: (e, person) => openDeleteDialog(person),
        },
    ];

    // -- Render ----------------------------------------------------------------

    const page = (
        <>
            <List
                tableRef={tableRef}
                columns={buildColumns(sponsorsMap)}
                actions={actions}
                getRows={getRows}
            />

            {/* Restore Dialog */}
            <Dialog open={restoreDialog.open} onClose={closeRestoreDialog}>
                <DialogTitle>Restaurar Inscricao</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja restaurar a inscricao de{" "}
                        <strong>{restoreDialog.person?.name}</strong>?
                        O registro voltara a aparecer na lista de inscricoes pendentes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRestoreDialog} color="default">
                        Cancelar
                    </Button>
                    <Button onClick={handleRestore} color="primary" variant="contained" autoFocus>
                        Restaurar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete permanente Dialog */}
            <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
                <DialogTitle style={{ color: "#ef4444" }}>Excluir Permanentemente</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir definitivamente a inscricao de{" "}
                        <strong>{deleteDialog.person?.name}</strong>?{" "}
                        <br /><br />
                        <strong style={{ color: "#ef4444" }}>
                            Esta acao e irreversivel. O registro sera removido do banco de dados e nao podera ser recuperado.
                        </strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="default" variant="outlined">
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color="secondary" variant="contained" autoFocus>
                        Excluir Permanentemente
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Person Dialog */}
            <Dialog
                open={Boolean(viewPerson)}
                onClose={() => setViewPerson(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 18 }}>Dados da Inscricao</span>
                    {viewPerson?.name && (
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                            {viewPerson.name}
                        </div>
                    )}
                </DialogTitle>
                <DialogContent style={{ paddingTop: 20 }}>
                    {viewPerson && <PersonDetail person={viewPerson} sponsorsMap={sponsorsMap} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewPerson(null)} color="default" variant="outlined">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    return <Sidebar current="Inscricoes Recusadas" content={page} />;
};

export default RejectedSubscriptions;
